"use client"

import * as React from "react"
import type { DateRange } from "react-day-picker"

import { Checkbox, CheckboxLabel } from "@/components/ui/checkbox"
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field"
import { RadioGroup, RadioGroupItem, RadioGroupLabel } from "@/components/ui/radio-group"
import { Input } from "@/components/ui/input"
import { LocalBanner } from "@/components/ui/banner"
import { DateRangePickerField } from "@/components/ui/date-picker-field"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  FloatingSheetPanel,
  FloatingSheetPanelBody,
  FloatingSheetPanelContent,
  FloatingSheetPanelHeader,
  FloatingSheetPanelToolbar,
  FloatingSheetPanelWorkflowFooter,
} from "@/lib/floating-sheet-panel"
import { LEARNING_ACTIVITY_OFFERINGS } from "@/lib/mock/learning-activities-offerings"
import {
  scheduleCoursesForTerm,
  scheduleTargetCount,
  syncedTermCourses,
} from "@/lib/mock/evaluation-schedule-courses"
import { DEMO_STATE_SETUP_TERM } from "@/lib/mock/evaluation-dashboard"
import { ExxatProductMark } from "@/components/exxat-product-logo"
import { SelectionTileGrid } from "@/components/ui/selection-tile-grid"
import {
  SURVEYS_ONBOARDING_PATH_TILES,
  SURVEYS_ONBOARDING_SURVEY_MODE_TILES,
  type SurveysOnboardingPath,
  type SurveysOnboardingSheetId,
  type SurveysOnboardingSurveyMode,
} from "@/lib/evaluation-dashboard-onboarding"
import { cn } from "@/lib/utils"

interface EvaluationDashboardOnboardingSheetsProps {
  activeSheet: SurveysOnboardingSheetId | null
  path: SurveysOnboardingPath | null
  onPathChange: (path: SurveysOnboardingPath) => void
  onOpenChange: (open: boolean) => void
  onComplete: (path?: SurveysOnboardingPath) => void
}

const ONBOARDING_COURSES = LEARNING_ACTIVITY_OFFERINGS.slice(0, 6)

const ONBOARDING_SURVEY_TEMPLATES = [
  {
    id: "eoc",
    name: "End of course evaluation",
    questionCount: 12,
  },
  {
    id: "clinical-site",
    name: "Clinical site evaluation",
    questionCount: 8,
  },
  {
    id: "faculty",
    name: "Faculty teaching evaluation",
    questionCount: 10,
  },
  {
    id: "preceptor",
    name: "Preceptor feedback",
    questionCount: 6,
  },
] as const

export function EvaluationDashboardOnboardingSheets({
  activeSheet,
  path,
  onPathChange,
  onOpenChange,
  onComplete,
}: EvaluationDashboardOnboardingSheetsProps) {
  return (
    <>
      <ConfirmTermSheet
        open={activeSheet === "confirm-term"}
        onOpenChange={onOpenChange}
        onComplete={onComplete}
      />
      <ImportPreviewSheet
        open={activeSheet === "import-preview"}
        onOpenChange={onOpenChange}
        onComplete={onComplete}
      />
      <TermSetupSheet
        open={activeSheet === "term-setup"}
        path={path}
        onPathChange={onPathChange}
        onOpenChange={onOpenChange}
        onComplete={onComplete}
      />
      <CoursesSheet
        open={activeSheet === "courses"}
        onOpenChange={onOpenChange}
        onComplete={onComplete}
      />
      <SurveySetupSheet
        open={activeSheet === "survey-setup"}
        onOpenChange={onOpenChange}
        onComplete={onComplete}
      />
      <ScheduleSheet
        open={activeSheet === "schedule"}
        onOpenChange={onOpenChange}
        onComplete={onComplete}
      />
    </>
  )
}

function SurveySetupSheet({
  open,
  onOpenChange,
  onComplete,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onComplete: (path?: SurveysOnboardingPath) => void
}) {
  const [draftMode, setDraftMode] = React.useState<SurveysOnboardingSurveyMode | null>(null)
  const [selectedTemplateId, setSelectedTemplateId] = React.useState<string>(
    ONBOARDING_SURVEY_TEMPLATES[0].id,
  )
  const [surveyName, setSurveyName] = React.useState("End of course evaluation")

  React.useEffect(() => {
    if (open) setDraftMode(null)
  }, [open])

  const surveyModeTileOptions = React.useMemo(
    () =>
      SURVEYS_ONBOARDING_SURVEY_MODE_TILES.map(tile => ({
        value: tile.value,
        label: tile.label,
        description: tile.description,
        leading: (
          <i
            className={cn("fa-light text-lg text-muted-foreground", tile.iconClass)}
            aria-hidden="true"
          />
        ),
      })),
    [],
  )

  const canSave =
    draftMode === "import"
      ? selectedTemplateId.length > 0
      : draftMode === "manual"
        ? surveyName.trim().length > 0
        : false

  return (
    <FloatingSheetPanel open={open} onOpenChange={onOpenChange}>
      <FloatingSheetPanelContent size="sm" contentSlot="surveys-onboarding-survey-setup">
        <FloatingSheetPanelToolbar />
        <FloatingSheetPanelHeader title="First survey" subtitle="First evaluation survey" />
        <FloatingSheetPanelBody className="flex flex-col gap-6 px-4 pb-4">
          <Field orientation="vertical">
            <FieldLabel>How do you want to set up your first survey?</FieldLabel>
            <SelectionTileGrid<SurveysOnboardingSurveyMode>
              className="w-full"
              aria-label="Survey setup mode"
              labelPlacement="below"
              options={surveyModeTileOptions}
              columns={2}
              value={draftMode ?? ("" as SurveysOnboardingSurveyMode)}
              onValueChange={setDraftMode}
              interaction="button"
            />
          </Field>

          {draftMode === "import" ? (
            <Field orientation="vertical">
              <FieldLabel>Template library</FieldLabel>
              <RadioGroup
                value={selectedTemplateId}
                onValueChange={setSelectedTemplateId}
                className="mt-2 flex flex-col divide-y divide-border rounded-lg border border-border"
                aria-label="Template library"
              >
                {ONBOARDING_SURVEY_TEMPLATES.map(template => {
                  const labelId = `onboarding-survey-template-${template.id}`
                  return (
                    <div key={template.id} className="flex items-start gap-3 px-3 py-3">
                      <RadioGroupItem
                        value={template.id}
                        id={labelId}
                        className="mt-0.5 shrink-0"
                      />
                      <RadioGroupLabel
                        htmlFor={labelId}
                        className="flex min-w-0 flex-1 flex-col items-start gap-0.5 py-0 text-sm font-normal"
                      >
                        <span className="font-medium text-foreground">{template.name}</span>
                        <span className="text-xs text-muted-foreground">
                          {template.questionCount} questions
                        </span>
                      </RadioGroupLabel>
                    </div>
                  )
                })}
              </RadioGroup>
            </Field>
          ) : null}

          {draftMode === "manual" ? (
            <Field orientation="vertical">
              <FieldLabel htmlFor="onboarding-survey-name">Survey name</FieldLabel>
              <Input
                id="onboarding-survey-name"
                value={surveyName}
                onChange={event => setSurveyName(event.target.value)}
              />
              <FieldDescription>You can add questions after setup from the Templates hub.</FieldDescription>
            </Field>
          ) : null}
        </FloatingSheetPanelBody>
        <FloatingSheetPanelWorkflowFooter
          onCancel={() => onOpenChange(false)}
          primaryLabel={
            draftMode === "import"
              ? "Use template"
              : draftMode === "manual"
                ? "Save survey"
                : "Continue"
          }
          onPrimary={() => {
            if (!canSave || draftMode == null) return
            onComplete()
          }}
          primaryDisabled={!canSave}
        />
      </FloatingSheetPanelContent>
    </FloatingSheetPanel>
  )
}

function TermSetupSheet({
  open,
  path,
  onPathChange,
  onOpenChange,
  onComplete,
}: {
  open: boolean
  path: SurveysOnboardingPath | null
  onPathChange: (path: SurveysOnboardingPath) => void
  onOpenChange: (open: boolean) => void
  onComplete: (path?: SurveysOnboardingPath) => void
}) {
  const [draftPath, setDraftPath] = React.useState<SurveysOnboardingPath | null>(path)
  const [termName, setTermName] = React.useState("Fall 2026")
  const [academicYear, setAcademicYear] = React.useState("2025-2026")
  const [range, setRange] = React.useState<DateRange | undefined>()
  const [prismProgram, setPrismProgram] = React.useState("summit-pa")
  const [importFileName, setImportFileName] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (open) setDraftPath(path)
  }, [open, path])

  const pathTileOptions = React.useMemo(
    () =>
      SURVEYS_ONBOARDING_PATH_TILES.map(tile => ({
        value: tile.value,
        label: tile.label,
        description: tile.description,
        leading:
          tile.value === "prism" ? (
            <span className="inline-flex size-6 items-center justify-center [&_svg]:!size-5 [&_svg]:max-h-5 [&_svg]:max-w-5">
              <ExxatProductMark product="exxat-prism" className="size-5 shrink-0" />
            </span>
          ) : (
            <i
              className={cn("fa-light text-lg text-muted-foreground", tile.iconClass)}
              aria-hidden="true"
            />
          ),
      })),
    [],
  )

  const canSave =
    draftPath != null &&
    (draftPath === "prism" ||
      (draftPath === "import" && importFileName != null) ||
      (draftPath === "manual" &&
        termName.trim().length > 0 &&
        Boolean(range?.from && range?.to)))

  return (
    <FloatingSheetPanel open={open} onOpenChange={onOpenChange}>
      <FloatingSheetPanelContent size="sm" contentSlot="surveys-onboarding-term-setup">
        <FloatingSheetPanelToolbar />
        <FloatingSheetPanelHeader
          title={draftPath === "prism" ? "Import from Exxat Prism" : "Set up term"}
          subtitle="Academic term"
        />
        <FloatingSheetPanelBody className="flex flex-col gap-6 px-4 pb-4">
          <Field orientation="vertical">
            <FieldLabel>How are you bringing data in?</FieldLabel>
            <SelectionTileGrid<SurveysOnboardingPath>
              className="w-full"
              aria-label="Setup path"
              labelPlacement="below"
              options={pathTileOptions}
              columns={3}
              value={draftPath ?? ("" as SurveysOnboardingPath)}
              onValueChange={setDraftPath}
              interaction="button"
            />
          </Field>

          {draftPath === "manual" ? (
            <>
              <Field orientation="vertical">
                <FieldLabel htmlFor="onboarding-term-name">Term name</FieldLabel>
                <Input
                  id="onboarding-term-name"
                  value={termName}
                  onChange={event => setTermName(event.target.value)}
                />
              </Field>
              <Field orientation="vertical">
                <FieldLabel htmlFor="onboarding-academic-year">Academic year</FieldLabel>
                <Input
                  id="onboarding-academic-year"
                  value={academicYear}
                  onChange={event => setAcademicYear(event.target.value)}
                />
              </Field>
              <Field orientation="vertical">
                <FieldLabel htmlFor="onboarding-term-range">Term dates</FieldLabel>
                <DateRangePickerField
                  id="onboarding-term-range"
                  value={range}
                  onChange={setRange}
                  numberOfMonths={2}
                />
                <FieldDescription>Pick start and end in one calendar.</FieldDescription>
              </Field>
            </>
          ) : null}

          {draftPath === "prism" ? (
            <>
              <Field orientation="vertical">
                <FieldLabel htmlFor="onboarding-prism-program">Prism program</FieldLabel>
                <Select value={prismProgram} onValueChange={setPrismProgram}>
                  <SelectTrigger id="onboarding-prism-program" className="w-full">
                    <SelectValue placeholder="Select program" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="summit-pa">
                      Summit PA · Doctor of Physical Therapy
                    </SelectItem>
                    <SelectItem value="lakeside-ot">
                      Lakeside OT · Master of Occupational Therapy
                    </SelectItem>
                  </SelectContent>
                </Select>
                <FieldDescription>Terms and course offerings sync from the selected program.</FieldDescription>
              </Field>
              <LocalBanner variant="info" title="Prism link is mock data in this preview">
                Production connects through your school Exxat Prism workspace.
              </LocalBanner>
            </>
          ) : null}

          {draftPath === "import" ? (
            <Field orientation="vertical">
              <FieldLabel htmlFor="onboarding-import-file">CSV file</FieldLabel>
              <Input
                id="onboarding-import-file"
                type="file"
                accept=".csv,text/csv"
                className="cursor-pointer file:me-3 file:rounded-md file:border-0 file:bg-muted file:px-3 file:py-1.5 file:text-sm file:font-medium"
                onChange={event => {
                  const file = event.target.files?.[0]
                  setImportFileName(file?.name ?? null)
                }}
              />
              <FieldDescription>
                Required columns: course number, course name, term start, term end.
              </FieldDescription>
              {importFileName ? (
                <LocalBanner variant="success" title="File ready">
                  {importFileName}
                </LocalBanner>
              ) : null}
            </Field>
          ) : null}
        </FloatingSheetPanelBody>
        <FloatingSheetPanelWorkflowFooter
          onCancel={() => onOpenChange(false)}
          primaryLabel={
            draftPath === "prism"
              ? "Import from Prism"
              : draftPath === "import"
                ? "Import file"
                : "Save term"
          }
          onPrimary={() => {
            if (!canSave || draftPath == null) return
            onPathChange(draftPath)
            onComplete(draftPath)
          }}
          primaryDisabled={!canSave}
        />
      </FloatingSheetPanelContent>
    </FloatingSheetPanel>
  )
}

function ConfirmTermSheet({
  open,
  onOpenChange,
  onComplete,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onComplete: (path?: SurveysOnboardingPath) => void
}) {
  const term = DEMO_STATE_SETUP_TERM
  const courses = React.useMemo(() => syncedTermCourses(term), [term])

  return (
    <FloatingSheetPanel open={open} onOpenChange={onOpenChange}>
      <FloatingSheetPanelContent size="sm" contentSlot="surveys-onboarding-confirm-term">
        <FloatingSheetPanelToolbar />
        <FloatingSheetPanelHeader
          title="Confirm synced term"
          subtitle={`${term.courseOfferings} courses · ${term.termName}`}
        />
        <FloatingSheetPanelBody className="flex flex-col gap-4 px-4 pb-4">
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-3 text-sm">
            <dt className="text-muted-foreground">Program</dt>
            <dd className="font-medium text-foreground">{term.programName}</dd>
            <dt className="text-muted-foreground">Academic year</dt>
            <dd className="font-medium text-foreground">{term.academicYear}</dd>
            <dt className="text-muted-foreground">Term</dt>
            <dd className="font-medium text-foreground">{term.termName}</dd>
            <dt className="text-muted-foreground">Dates</dt>
            <dd className="font-medium text-foreground">
              {term.startDate} to {term.endDate}
            </dd>
          </dl>
          <ul className="flex flex-col divide-y divide-border rounded-lg border border-border">
            {courses.map(course => (
              <li key={course.id} className="flex flex-col gap-0.5 px-3 py-3">
                <span className="font-mono text-sm tabular-nums text-foreground">
                  {course.courseCode}
                </span>
                <span className="text-sm text-foreground">{course.title}</span>
                <span className="text-xs text-muted-foreground">{course.facultyName}</span>
              </li>
            ))}
          </ul>
        </FloatingSheetPanelBody>
        <FloatingSheetPanelWorkflowFooter
          onCancel={() => onOpenChange(false)}
          primaryLabel="Confirm term"
          onPrimary={() => onComplete()}
        />
      </FloatingSheetPanelContent>
    </FloatingSheetPanel>
  )
}

const IMPORT_PREVIEW_ROWS = ONBOARDING_COURSES.slice(0, 4)

function ImportPreviewSheet({
  open,
  onOpenChange,
  onComplete,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onComplete: (path?: SurveysOnboardingPath) => void
}) {
  return (
    <FloatingSheetPanel open={open} onOpenChange={onOpenChange}>
      <FloatingSheetPanelContent size="sm" contentSlot="surveys-onboarding-import-preview">
        <FloatingSheetPanelToolbar />
        <FloatingSheetPanelHeader
          title="Validate import"
          subtitle={`${IMPORT_PREVIEW_ROWS.length} of ${ONBOARDING_COURSES.length} rows shown`}
        />
        <FloatingSheetPanelBody className="px-4 pb-4">
          <ul className="flex flex-col divide-y divide-border rounded-lg border border-border">
            {IMPORT_PREVIEW_ROWS.map(course => (
              <li key={course.id} className="flex flex-col gap-0.5 px-3 py-3">
                <span className="font-mono text-sm tabular-nums text-foreground">
                  {course.courseNumber}
                </span>
                <span className="text-sm text-foreground">{course.courseName}</span>
                <span className="text-xs text-muted-foreground">
                  {course.term} · {course.cohort}
                </span>
              </li>
            ))}
          </ul>
        </FloatingSheetPanelBody>
        <FloatingSheetPanelWorkflowFooter
          onCancel={() => onOpenChange(false)}
          primaryLabel="Import courses"
          onPrimary={() => onComplete()}
        />
      </FloatingSheetPanelContent>
    </FloatingSheetPanel>
  )
}

function CoursesSheet({
  open,
  onOpenChange,
  onComplete,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onComplete: (path?: SurveysOnboardingPath) => void
}) {
  const [selectedIds, setSelectedIds] = React.useState<string[]>(() =>
    ONBOARDING_COURSES.map(course => course.id),
  )

  return (
    <FloatingSheetPanel open={open} onOpenChange={onOpenChange}>
      <FloatingSheetPanelContent size="sm" contentSlot="surveys-onboarding-courses">
        <FloatingSheetPanelToolbar />
        <FloatingSheetPanelHeader
          title="Connect course offerings"
          subtitle="Course offerings"
          description="Select courses that will receive evaluations this term."
        />
        <FloatingSheetPanelBody className="px-4 pb-4">
          <ul className="flex flex-col divide-y divide-border rounded-lg border border-border">
            {ONBOARDING_COURSES.map(course => {
              const checked = selectedIds.includes(course.id)
              const labelId = `onboarding-course-${course.id}`
              return (
                <li key={course.id} className="flex items-start gap-3 px-3 py-3">
                  <Checkbox
                    id={labelId}
                    checked={checked}
                    onCheckedChange={value => {
                      setSelectedIds(current =>
                        value
                          ? [...current, course.id]
                          : current.filter(id => id !== course.id),
                      )
                    }}
                  />
                  <div className="min-w-0 flex-1">
                    <CheckboxLabel htmlFor={labelId} className="flex flex-col items-start gap-0.5">
                      <span className="font-mono text-sm tabular-nums text-foreground">
                        {course.courseNumber}
                      </span>
                      <span className="text-sm text-foreground">{course.courseName}</span>
                      <span className="text-xs text-muted-foreground">
                        {course.term} · {course.cohort}
                      </span>
                    </CheckboxLabel>
                  </div>
                </li>
              )
            })}
          </ul>
        </FloatingSheetPanelBody>
        <FloatingSheetPanelWorkflowFooter
          onCancel={() => onOpenChange(false)}
          primaryLabel="Connect courses"
          onPrimary={() => {
            if (selectedIds.length === 0) return
            onComplete()
          }}
          primaryDisabled={selectedIds.length === 0}
        />
      </FloatingSheetPanelContent>
    </FloatingSheetPanel>
  )
}

function ScheduleSheet({
  open,
  onOpenChange,
  onComplete,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onComplete: (path?: SurveysOnboardingPath) => void
}) {
  const term = DEMO_STATE_SETUP_TERM
  const courses = React.useMemo(() => scheduleCoursesForTerm(term, ["notSetup", "draft"]), [term])
  const [selectedIds, setSelectedIds] = React.useState<string[]>(() =>
    courses.map(course => course.id),
  )
  const targetCount = scheduleTargetCount(term, ["notSetup", "draft"])

  return (
    <FloatingSheetPanel open={open} onOpenChange={onOpenChange}>
      <FloatingSheetPanelContent size="sm" contentSlot="surveys-onboarding-schedule">
        <FloatingSheetPanelToolbar />
        <FloatingSheetPanelHeader
          title="Schedule evaluations"
          subtitle={`${term.academicYear} · ${term.termName}`}
          description={`${selectedIds.length} of ${targetCount} courses selected`}
        />
        <FloatingSheetPanelBody className="px-4 pb-4">
          <ul className="flex flex-col divide-y divide-border rounded-lg border border-border">
            {courses.map(course => {
              const labelId = `onboarding-schedule-${course.id}`
              return (
                <li key={course.id} className="flex items-start gap-3 px-3 py-3">
                  <Checkbox
                    id={labelId}
                    checked={selectedIds.includes(course.id)}
                    onCheckedChange={value => {
                      setSelectedIds(current =>
                        value
                          ? [...current, course.id]
                          : current.filter(id => id !== course.id),
                      )
                    }}
                  />
                  <div className="min-w-0 flex-1">
                    <CheckboxLabel htmlFor={labelId} className="flex flex-col items-start gap-0.5">
                      <span className="font-mono text-sm tabular-nums text-foreground">
                        {course.courseCode}
                      </span>
                      <span className="text-sm text-foreground">{course.title}</span>
                    </CheckboxLabel>
                  </div>
                </li>
              )
            })}
          </ul>
        </FloatingSheetPanelBody>
        <FloatingSheetPanelWorkflowFooter
          onCancel={() => onOpenChange(false)}
          primaryLabel="Schedule selected"
          onPrimary={() => {
            if (selectedIds.length === 0) return
            onComplete()
          }}
          primaryDisabled={selectedIds.length === 0}
        />
      </FloatingSheetPanelContent>
    </FloatingSheetPanel>
  )
}
